/**
 * F5.3 Wave-4 — T-SQL datepart vocabulary at DATEADD/DATEPART/DATEDIFF/DATENAME
 * first-argument slot.
 *
 * The first argument of these four built-ins (plus DATETRUNC / DATE_BUCKET on
 * newer SQL Server versions) is a known-vocabulary slot — the datepart token —
 * not a column reference. The grammar's clause classifier maps the function-
 * argument position to `'select-list'` / `'unknown'` / etc., so the resolver
 * runs `isMssqlDatepartPosition` as a narrow text-walk fallback. When matched,
 * the curated datepart list tops the suggestion set and short-circuits.
 *
 * Closes F5.3 R-bucket gaps script.54 §1-§7 (DATEADD / DATEPART / DATEDIFF /
 * DATENAME first-arg) — see goals/autocomplete-coverage/gaps/mssql.md.
 */

import { tokenize, type Token } from '../context/tokenizer';

const ENGINE = 'mssql' as const;

/**
 * Curated T-SQL datepart vocabulary. Order = display order (longest forms
 * first, then dialect abbreviations). Covers every datepart accepted by
 * DATEADD / DATEPART / DATEDIFF / DATENAME per the SQL Server reference.
 */
const MSSQL_DATEPARTS: readonly string[] = [
  'year', 'yyyy', 'yy',
  'quarter', 'qq', 'q',
  'month', 'mm', 'm',
  'dayofyear', 'dy', 'y',
  'day', 'dd', 'd',
  'week', 'wk', 'ww',
  'weekday', 'dw', 'w',
  'hour', 'hh',
  'minute', 'mi', 'n',
  'second', 'ss', 's',
  'millisecond', 'ms',
  'microsecond', 'mcs',
  'nanosecond', 'ns',
  'iso_week', 'isowk', 'isoww',
  'tzoffset', 'tz',
];

const DATEPART_FUNCTIONS: ReadonlySet<string> = new Set([
  'DATEADD',
  'DATEPART',
  'DATEDIFF',
  'DATEDIFF_BIG',
  'DATENAME',
  'DATETRUNC',
]);

export function getMssqlDateparts(): readonly string[] {
  return MSSQL_DATEPARTS;
}

/**
 * `CONVERT(<type>, <expr>, ▮)` — the optional third argument is a STYLE
 * ORDINAL from a closed, documented set, not an expression. Nothing else in
 * the completion surface can produce it: the tokens are bare integers, so
 * they are neither columns, keywords, nor functions, and the position sits in
 * a named clause (`select-list` for the coverage script) where the generic
 * emission floods columns instead (mssql script.53 §3).
 *
 * Display order is "most-used first, then ascending" rather than numeric —
 * a style list sorted by value buries `121` (the ODBC canonical form almost
 * everyone wants) under a dozen legacy regional codes.
 *
 * Each entry carries the format it produces as its detail text, which is the
 * only way the ordinal is legible at the call site.
 */
const MSSQL_CONVERT_STYLES: readonly { readonly code: string; readonly meaning: string }[] = [
  { code: '121', meaning: 'ODBC canonical (ms) — yyyy-mm-dd hh:mi:ss.mmm' },
  { code: '126', meaning: 'ISO8601 — yyyy-mm-ddThh:mi:ss.mmm' },
  { code: '127', meaning: 'ISO8601 with time zone Z' },
  { code: '120', meaning: 'ODBC canonical — yyyy-mm-dd hh:mi:ss' },
  { code: '23', meaning: 'ISO8601 date — yyyy-mm-dd' },
  { code: '101', meaning: 'USA — mm/dd/yyyy' },
  { code: '103', meaning: 'British/French — dd/mm/yyyy' },
  { code: '104', meaning: 'German — dd.mm.yyyy' },
  { code: '105', meaning: 'Italian — dd-mm-yyyy' },
  { code: '106', meaning: 'dd mon yyyy' },
  { code: '107', meaning: 'mon dd, yyyy' },
  { code: '108', meaning: 'hh:mi:ss' },
  { code: '109', meaning: 'default + milliseconds' },
  { code: '110', meaning: 'USA — mm-dd-yyyy' },
  { code: '111', meaning: 'Japan — yyyy/mm/dd' },
  { code: '112', meaning: 'ISO — yyyymmdd' },
  { code: '113', meaning: 'Europe default + ms — dd mon yyyy hh:mi:ss.mmm' },
  { code: '114', meaning: 'hh:mi:ss.mmm (24 hour)' },
  { code: '130', meaning: 'Hijri — dd mon yyyy hh:mi:ss.mmm' },
  { code: '131', meaning: 'Hijri — dd/mm/yyyy hh:mi:ss.mmm' },
  { code: '0', meaning: 'default — mon dd yyyy hh:miAM (also: numeric/binary style 0)' },
  { code: '1', meaning: 'USA — mm/dd/yy (also: numeric/binary style 1)' },
  { code: '2', meaning: 'ANSI — yy.mm.dd (also: numeric/binary style 2)' },
  { code: '3', meaning: 'British/French — dd/mm/yy' },
  { code: '4', meaning: 'German — dd.mm.yy' },
  { code: '100', meaning: 'default with century — mon dd yyyy hh:miAM' },
];

export function getMssqlConvertStyles(): readonly {
  readonly code: string;
  readonly meaning: string;
}[] {
  return MSSQL_CONVERT_STYLES;
}

/** The functions whose third argument is a CONVERT style ordinal. */
const CONVERT_STYLE_FUNCTIONS: ReadonlySet<string> = new Set(['CONVERT', 'TRY_CONVERT']);

/**
 * Detect whether the cursor sits at the third-argument slot of
 * `CONVERT` / `TRY_CONVERT`. Same conservatism as the datepart detector: the
 * enclosing unclosed `(` must belong to one of those callees, and the cursor
 * must sit after exactly two commas at that paren depth (a nested call's
 * commas are at a deeper depth and do not count).
 */
export function isMssqlConvertStylePosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const call = enclosingCallArgIndex(source, cursor, statementStart);
  if (!call) return false;
  return CONVERT_STYLE_FUNCTIONS.has(call.callee) && call.argIndex === 2;
}

/**
 * Detect whether the cursor sits at the first-argument slot of one of the
 * datepart-consuming built-ins. Conservative — returns false when the
 * enclosing call isn't datepart-shaped or the cursor has crossed past the
 * first comma at this paren depth.
 */
export function isMssqlDatepartPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const call = enclosingCallArgIndex(source, cursor, statementStart);
  if (!call) return false;
  return DATEPART_FUNCTIONS.has(call.callee) && call.argIndex === 0;
}

/**
 * The call the cursor is inside, and which of its arguments the cursor sits
 * at. `argIndex` counts commas at the call's OWN paren depth, so a nested
 * call's arguments do not shift the outer index.
 *
 * Returns null when the cursor is not inside an unclosed `(` whose immediately
 * preceding token is an identifier/keyword (i.e. not a call at all — a
 * grouping paren, a subquery, a type modifier).
 */
function enclosingCallArgIndex(
  source: string,
  cursor: number,
  statementStart: number,
): { readonly callee: string; readonly argIndex: number } | null {
  const tokens = tokenize(source, statementStart, cursor, ENGINE);
  if (tokens.length === 0) return null;

  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return null;

  // Token immediately before the unclosed `(` must be the callee name.
  const callee = tokens[lparenIdx - 1];
  if (!callee || (callee.type !== 'ident' && callee.type !== 'kw')) return null;

  let depth = 0;
  let argIndex = 0;
  for (let i = lparenIdx + 1; i < tokens.length; i += 1) {
    const t = tokens[i]!;
    if (t.type === 'lparen') depth += 1;
    else if (t.type === 'rparen') depth -= 1;
    else if (t.type === 'comma' && depth === 0) argIndex += 1;
  }
  return { callee: callee.text.toUpperCase(), argIndex };
}

function findEnclosingLparenIndex(tokens: readonly Token[]): number {
  let depth = 0;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.type === 'rparen') depth += 1;
    else if (t.type === 'lparen') {
      if (depth === 0) return i;
      depth -= 1;
    }
  }
  return -1;
}
